import { useFormik } from "formik"
import PropTypes from "prop-types"
import React from "react"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import Select from "react-select"
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import * as Yup from "yup"

export const CLIENT_LIST = [
  { value: "acme", label: "Acme Corporation" },
  { value: "techventures", label: "TechVentures Ltd" },
  { value: "global", label: "Global Systems Inc" },
  { value: "datastream", label: "DataStream Corp" },
  { value: "nextgen", label: "NextGen Solutions" },
  { value: "brightwave", label: "Brightwave Technologies" },
  { value: "alphasoft", label: "AlphaSoft Inc" },
]

export const AI_ASSIST_TYPES = [
  { value: "AI-Assist-Bundle", label: "AI-Assist-Bundle" },
  { value: "AI-Assist-Separate", label: "AI-Assist-Separate" },
]

const toDateObj = val => (val ? new Date(val) : null)

const InvoiceFormModal = ({ isOpen, toggle, isEdit, selectedInvoice, onSave }) => {
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      clientId:      isEdit ? selectedInvoice?.clientId      ?? "" : "",
      clientName:    isEdit ? selectedInvoice?.clientName    ?? "" : "",
      invoiceDate:   isEdit ? toDateObj(selectedInvoice?.invoiceDate)  : null,
      aiAssistType:  isEdit ? selectedInvoice?.aiAssistType  ?? "" : "",
      fromDate:      isEdit ? toDateObj(selectedInvoice?.fromDate)     : null,
      toDate:        isEdit ? toDateObj(selectedInvoice?.toDate)       : null,
      amount:        isEdit ? selectedInvoice?.amount        ?? ""  : "",
    },
    validationSchema: Yup.object({
      clientName:   Yup.string().required("Please select a client"),
      invoiceDate:  Yup.date().nullable().required("Please select invoice date"),
      aiAssistType: Yup.string().required("Please select AI Assist Type"),
      fromDate:     Yup.date().nullable().required("Please select from date"),
      toDate:       Yup.date()
        .nullable()
        .required("Please select to date")
        .min(Yup.ref("fromDate"), "To Date must be after From Date"),
      amount: Yup.number()
        .typeError("Amount must be a number")
        .positive("Amount must be positive")
        .required("Please enter amount"),
    }),
    onSubmit: values => {
      const toISO = d => (d instanceof Date ? d.toISOString().split("T")[0] : d)
      const record = {
        id:           isEdit ? selectedInvoice.id : Date.now(),
        clientId:     values.clientId,
        clientName:   values.clientName,
        invoiceDate:  toISO(values.invoiceDate),
        aiAssistType: values.aiAssistType,
        fromDate:     toISO(values.fromDate),
        toDate:       toISO(values.toDate),
        amount:       parseFloat(values.amount),
      }
      onSave(record)
      validation.resetForm()
      toggle()
    },
  })

  const selectedClient =
    CLIENT_LIST.find(c => c.value === validation.values.clientId) || null

  const selectedType =
    AI_ASSIST_TYPES.find(t => t.value === validation.values.aiAssistType) || null

  const handleClose = () => {
    validation.resetForm()
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" centered>
      <ModalHeader toggle={handleClose} tag="h4">
        {isEdit ? "Edit AI Invoice" : "Add New AI Invoice"}
      </ModalHeader>

      <ModalBody>
        <Form
          onSubmit={e => {
            e.preventDefault()
            validation.handleSubmit()
          }}
        >
          {/* Row 1 — Client & Invoice Date */}
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">
                  Client Name <span className="text-danger">*</span>
                </Label>
                <Select
                  options={CLIENT_LIST}
                  value={selectedClient}
                  onChange={opt => {
                    validation.setFieldValue("clientId",   opt?.value ?? "")
                    validation.setFieldValue("clientName", opt?.label ?? "")
                  }}
                  onBlur={() => validation.setFieldTouched("clientName", true)}
                  placeholder="Select client..."
                  classNamePrefix="select2-selection"
                  isClearable
                />
                {validation.touched.clientName && validation.errors.clientName && (
                  <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>
                    {validation.errors.clientName}
                  </div>
                )}
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">
                  Invoice Date <span className="text-danger">*</span>
                </Label>
                <DatePicker
                  selected={validation.values.invoiceDate}
                  onChange={date => validation.setFieldValue("invoiceDate", date)}
                  onBlur={() => validation.setFieldTouched("invoiceDate", true)}
                  dateFormat="dd MMM yyyy"
                  placeholderText="Select invoice date"
                  autoComplete="off"
                  className={
                    "form-control" +
                    (validation.touched.invoiceDate && validation.errors.invoiceDate
                      ? " is-invalid"
                      : "")
                  }
                />
                {validation.touched.invoiceDate && validation.errors.invoiceDate && (
                  <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>
                    {validation.errors.invoiceDate}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Row 2 — AI Assist Type & Amount */}
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">
                  AI Assist Type <span className="text-danger">*</span>
                </Label>
                <Select
                  options={AI_ASSIST_TYPES}
                  value={selectedType}
                  onChange={opt =>
                    validation.setFieldValue("aiAssistType", opt?.value ?? "")
                  }
                  onBlur={() => validation.setFieldTouched("aiAssistType", true)}
                  placeholder="Select type..."
                  classNamePrefix="select2-selection"
                  isClearable
                />
                {validation.touched.aiAssistType && validation.errors.aiAssistType && (
                  <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>
                    {validation.errors.aiAssistType}
                  </div>
                )}
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">
                  Amount (USD) <span className="text-danger">*</span>
                </Label>
                <Input
                  name="amount"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.amount}
                  invalid={
                    validation.touched.amount && validation.errors.amount ? true : false
                  }
                />
                {validation.touched.amount && validation.errors.amount && (
                  <FormFeedback type="invalid">{validation.errors.amount}</FormFeedback>
                )}
              </div>
            </Col>
          </Row>

          {/* Row 3 — From Date & To Date */}
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">
                  From Date <span className="text-danger">*</span>
                </Label>
                <DatePicker
                  selected={validation.values.fromDate}
                  onChange={date => {
                    validation.setFieldValue("fromDate", date)
                    // Reset toDate if it's before the new fromDate
                    if (
                      validation.values.toDate &&
                      date &&
                      validation.values.toDate < date
                    ) {
                      validation.setFieldValue("toDate", null)
                    }
                  }}
                  onBlur={() => validation.setFieldTouched("fromDate", true)}
                  dateFormat="dd MMM yyyy"
                  placeholderText="Select from date"
                  autoComplete="off"
                  className={
                    "form-control" +
                    (validation.touched.fromDate && validation.errors.fromDate
                      ? " is-invalid"
                      : "")
                  }
                />
                {validation.touched.fromDate && validation.errors.fromDate && (
                  <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>
                    {validation.errors.fromDate}
                  </div>
                )}
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">
                  To Date <span className="text-danger">*</span>
                </Label>
                <DatePicker
                  selected={validation.values.toDate}
                  onChange={date => validation.setFieldValue("toDate", date)}
                  onBlur={() => validation.setFieldTouched("toDate", true)}
                  dateFormat="dd MMM yyyy"
                  placeholderText="Select to date"
                  autoComplete="off"
                  minDate={validation.values.fromDate || undefined}
                  className={
                    "form-control" +
                    (validation.touched.toDate && validation.errors.toDate
                      ? " is-invalid"
                      : "")
                  }
                />
                {validation.touched.toDate && validation.errors.toDate && (
                  <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>
                    {validation.errors.toDate}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Footer */}
          <Row>
            <Col>
              <div className="text-end">
                <Button
                  type="button"
                  color="secondary"
                  className="me-2"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button type="submit" color="primary">
                  {isEdit ? "Update Invoice" : "Save Invoice"}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  )
}

InvoiceFormModal.propTypes = {
  isOpen:           PropTypes.bool.isRequired,
  toggle:           PropTypes.func.isRequired,
  isEdit:           PropTypes.bool.isRequired,
  selectedInvoice:  PropTypes.object,
  onSave:           PropTypes.func.isRequired,
}

export default InvoiceFormModal
